import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import SEOHead from "@/components/SEOHead";
import SEORelatedPages from "@/components/SEORelatedPages";
import { getSEOData } from "@/lib/seo-pages";

const seo = getSEOData("/pesnya-na-den-rozhdeniya")!;

const CTABlock = () => (
  <div className="my-10 text-center">
    <Button size="lg" className="shadow-soft rounded-full px-8 text-base" asChild>
      <Link to="/create">
        🔥 Создать песню на день рождения
      </Link>
    </Button>
    <p className="text-muted-foreground text-sm mt-3">
      Демоверсия бесплатна. Без установки приложений.
    </p>
  </div>
);

const PesnyaNaDenRozhdeniya = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={seo.title}
        description={seo.description}
        canonical={seo.canonical}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: seo.faq?.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
          })),
        }}
      />
      <Navbar />
      <main className="pt-24 pb-16">
        <article className="container px-4 mx-auto max-w-3xl">
          <h1 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold mb-6 text-foreground leading-tight">
            Песня на день рождения на заказ — персональный музыкальный подарок
          </h1>

          <div className="space-y-4 text-muted-foreground leading-relaxed mb-8">
            <p>
              Хотите сделать день рождения по-настоящему запоминающимся?{" "}
              <strong className="text-foreground">Персональная песня на день рождения</strong> — это оригинальный и эмоциональный подарок, который вызывает улыбку, удивление и настоящую вау-реакцию.
            </p>
            <p>
              Сервис HitChef позволяет создать индивидуальный хит всего за 3 минуты. Вам не нужно быть музыкантом — просто ответьте на несколько вопросов, и мы создадим песню специально для именинника.
            </p>
          </div>

          <CTABlock />

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-4 text-foreground">
              Почему песня на день рождения — лучший подарок?
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-4">
              Обычные подарки быстро забываются. А персональная песня остаётся навсегда.
            </p>
            <ul className="space-y-2 text-muted-foreground">
              <li>🎉 Можно включить на празднике</li>
              <li>😂 Добавить смешные истории</li>
              <li>🎁 Сделать сюрприз для друга</li>
              <li>❤️ Передать тёплые слова</li>
            </ul>
            <p className="text-foreground font-medium mt-4">Это не просто трек — это эмоция.</p>
          </section>

          <CTABlock />

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-4 text-foreground">
              Для кого подойдёт персональная песня?
            </h2>
            <ul className="space-y-2 text-muted-foreground mb-4">
              <li>• Для мужа или жены</li>
              <li>• Для <Link to="/pesnya-devushke-v-podarok" className="text-primary hover:underline">девушки</Link> или парня</li>
              <li>• Для <Link to="/pesnya-pro-druga" className="text-primary hover:underline">друга</Link> или подруги</li>
              <li>• Для <Link to="/pesnya-mame-v-podarok" className="text-primary hover:underline">родителей</Link></li>
              <li>• Для <Link to="/pesnya-kollege-v-podarok" className="text-primary hover:underline">коллеги</Link></li>
            </ul>
            <p className="text-muted-foreground leading-relaxed">
              Песня может быть весёлой, романтичной, трогательной или даже шуточной — вы сами выбираете стиль.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-4 text-foreground">
              Как создать песню на день рождения?
            </h2>
            <ol className="space-y-2 text-muted-foreground list-decimal list-inside">
              <li>Нажмите кнопку «Создать хит»</li>
              <li>Выберите событие и жанр</li>
              <li>Ответьте на 3 простых вопроса</li>
              <li>Получите демоверсию бесплатно</li>
            </ol>
            <p className="text-muted-foreground mt-4">После прослушивания вы можете получить полную версию песни.</p>
          </section>

          <CTABlock />

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-4 text-foreground">
              Почему выбирают HitChef?
            </h2>
            <ul className="space-y-2 text-muted-foreground">
              <li>⚡ <strong className="text-foreground">Быстро</strong> — всего 3 минуты</li>
              <li>✅ <strong className="text-foreground">Просто</strong> — без сложных настроек</li>
              <li>🎯 <strong className="text-foreground">Персонально</strong> — текст создаётся под конкретного человека</li>
              <li>📱 <strong className="text-foreground">Онлайн</strong> — работает с телефона</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="font-display text-xl sm:text-2xl font-bold mb-6 text-foreground">
              Часто задаваемые вопросы
            </h2>
            <Accordion type="single" collapsible className="space-y-2">
              {seo.faq?.map((item, i) => (
                <AccordionItem
                  key={i}
                  value={`faq-${i}`}
                  className="border border-border rounded-xl px-5 bg-card"
                >
                  <AccordionTrigger className="text-left hover:no-underline text-sm font-medium text-foreground">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-sm leading-relaxed">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <CTABlock />

          <SEORelatedPages pages={seo.relatedPages || []} />
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default PesnyaNaDenRozhdeniya;
